import './App.css';
import React from 'react';


/* Components */
import Modal from './components/modal';
import TimelineComponent from './components/timeline';
import LearnMoreComponent from './components/learn-more';
import MapComponent from './components/map';
import LoadingComponent from './components/loading';
import HeaderComponent from './components/header';
import EntryCountComponent from './components/entry-count-component';
import CollapsableSidebar from './components/map-sidebar';

/* Modal Views */
import About from './modals/about';
import AddressSearch from './modals/search-modal-content';

/* Util */
import debounce from 'lodash.debounce';

import Context from './context';

import mapUtil from './map-util';

const queryParams = new URLSearchParams(window.location.hash.split("?")[1]);
const debugState = queryParams.has('debugState');
const MapContext = Context;
const canvasSelector = 'canvas';


class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      debugState: debugState,
      timelineEntries: [],
      modalOpen: true,
      modalContent: "search",
      zoomIn: true,
      addressState: {
        addressInput: "",
        addressList: []
      }
    }

    this.tryGetFeaturesInViewDebounced = debounce(this.tryGetFeaturesInView, 500, { leading: false, trailing: true });
    this.handleExtentStateChangeDebounced = debounce(this.handleExtentStateChange, 500, { leading: false, trailing: true });

    window.onbeforeprint = () => {
      this.setState({printing: true });
    }

    window.onafterprint = () => {
      this.setState({ printing: false });
      setTimeout(() => {
        this.state.mapObj.updateSize();
      }, 200);
    }
  }


  handleExtentStateChange = (evt) => { 
    let { extent, zoom, center } = mapUtil.unwrapExtentChange(evt); 
    this.handleExtentChange(extent, zoom, center) 
  }

  /*
    This will call tot he map util to create a map if it doesnt exist
    Otherwise it will reset the target of the map and retrn it
    It is important to reset the target in this case as the map element probably got destroyed by the shadow dom
  */
  createMapIfNotExists = (target) => {
    if (this.state.mapObj === undefined) {
      let map = mapUtil.createMap(target);


      this.setState({ mapObj: map });

      map.on('moveend', this.handleExtentStateChangeDebounced );
      map.on('click', (evt) => { let pixel = mapUtil.unwrapMapClick(evt); this.handleMapClick(pixel) } );

      window.mapObj = map;

      return map;

    }

    // This will get called when the map is already created, often this is due to development when a change to the map component is made.
    this.state.mapObj.setTarget(null);
    this.state.mapObj.setTarget('map');

    return this.state.mapObj;
  }

  useCategories = (categories) => {
    this.setState({ categories: categories });
  }

  // Set the image to a single transparent pixel
  clearPrintImage = () => {
    let img = document.getElementById("print-map");
    img.setAttribute('src', 'data:image/gif;base64,R0lGODlhAQABAIAAAP///wAAACH5BAEAAAAALAAAAAABAAEAAAICRAEAOw==');
  }
  
  /*
    This function will cause a render to a separate canvas. The image is exported and then assigned to an img tag.
    We need to do this because when a print is called the canvas gets resized but openlayers doesnt have time to update the map
    Additionally if we allowed the canvas to resize the extent would change and the information would be invalid

  */
  updatePrintImage = () => {
    this.setState({rendering: true})
    let img = null;

    img = document.getElementById("print-map");

    img.setAttribute('loading', 'lazy');

    this.state.mapObj.once('rendercomplete', () => {

      let mapCanvases = this.state.mapObj.getViewport().querySelectorAll(canvasSelector);
      let combinedCanvas = window.combinedCanvas || document.createElement('canvas');

      let targetWidth = -1;
      let targetHeight = -1;

      if(mapCanvases.length > 0) {

        mapCanvases.forEach(canvas => {
          if(canvas.width > targetWidth) {
            targetWidth = canvas.width;
            targetHeight = canvas.height;
          }
        });

        combinedCanvas.width = targetWidth;
        combinedCanvas.height = targetHeight;
      }

      let ctx = combinedCanvas.getContext('2d');

      mapCanvases.forEach(canvas => {
        ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
      });

      let img_render = combinedCanvas.toDataURL("image/png");
      
      this.setState({ mapImage: img_render, rendering: false});
      document.getElementById('print-map').setAttribute('src', img_render);
      this.state.mapObj.updateSize();
      
    });

    this.state.mapObj.renderSync();
  }

  updatePrintImageAsync = async () => {
    console.log("Updating print image");

    this.setState({rendering: true})

    // Create a promise that will resolve when the map is rendered
    let renderPromise = new Promise((resolve, reject) => {
      this.state.mapObj.once('rendercomplete', () => {

        let mapCanvases = this.state.mapObj.getViewport().querySelectorAll(canvasSelector);
        let combinedCanvas = window.combinedCanvas || document.createElement('canvas');
  
        let targetWidth = -1;
        let targetHeight = -1;
  
        if(mapCanvases.length > 0) {
  
          mapCanvases.forEach(canvas => {
            if(canvas.width > targetWidth) {
              targetWidth = canvas.width;
              targetHeight = canvas.height;
            }
          });
  
          combinedCanvas.width = targetWidth;
          combinedCanvas.height = targetHeight;
        }
  
        let ctx = combinedCanvas.getContext('2d');
  
        mapCanvases.forEach(canvas => {
          const opacity =
            canvas.parentNode.style.opacity || canvas.style.opacity;
          ctx.globalAlpha = opacity === '' ? 1 : Number(opacity);

          ctx.drawImage(canvas, 0, 0, targetWidth, targetHeight);
        });
  
        let img_render = combinedCanvas.toDataURL("image/png");
        
        this.setState({ mapImage: img_render, rendering: false});
        document.getElementById('print-map').setAttribute('src', img_render);
        this.state.mapObj.updateSize();

        resolve();
        
      });
    });

    this.state.mapObj.renderSync();

    return renderPromise;
    
  }

  /*
    Here we get the build info for the frontend
    This is used to display the version of the app
  */
  componentDidMount() {
    fetch("/buildInfo.json").then(r => r.json().then(buildInfo => {
      this.setState({ buildInfo: buildInfo });
    }));
  }

  handleExtentChange = async (bbox, zoom, center) => {
    // Update our state to keep track of the position
    this.setState({
      lat: center[1],
      lon: center[0],
      zoom: zoom,
      extent: bbox
    });
    
    if (this.state.printing || this.state.rendering)
      return;

    this.unhighlightAllFeatures();

    this.clearPrintImage();

    this.setState({ zoom });
    if (zoom <= 10) {
      this.setState({
        timelineEntries: [],
        searching: false,
        zoomIn: true
      });
      return;
    }

    this.setState({ bbox, extent: bbox, zoomIn: false });
    this.tryGetFeaturesInViewDebounced();
  }

  handleMapClick = async (pixel) => {
    let features = await this.state.mapObj.themes.getCategoryByKey("cat_mvt").getLayerByKey("lyr_public_fires_mvt").getFeaturesUnderPixel(pixel);
    this.featureClicked(features);
  }


  /*
    This function converts an openlayers render feature into the data structure we expect for the timeline
  */
  convertOLRenderFeatureToTimelineEntry = (feat) => {
    let discoveryDate = feat.properties_.discoverydate;
    let outDate = feat.properties_.outdate;

    try {
      discoveryDate = new Date(discoveryDate);
      if (discoveryDate.toString() === "Invalid Date")
        discoveryDate = null;

      outDate = new Date(outDate);
      if (outDate.toString() === "Invalid Date")
        outDate = null;
    }
    catch (e) {
      console.error("Error parsing date", e);
    }


    return {
      id: feat.id_,
      name: feat.properties_.name,
      discoveryDate,
      outDate,
      acres: feat.properties_.calculatedacres,
      otherStructuresDestroyed: feat.properties_.otherstructuresdestroyed,
      otherStructuresThreatened: feat.properties_.otherstructuresthreatened,
      residencesDestroyed: feat.properties_.residencesdestroyed,
      residencesThreatened: feat.properties_.residencesthreatened,
      generalCause: feat.properties_.generalcause,
      specificCause: feat.properties_.specificcause,
    }
  }

  sortByDiscoveryDate = (a, b) => {
    return b.discoveryDate - a.discoveryDate;
  }


  tryGetFeaturesInView = async () => {
    
    this.setState( {searching: true } );


    try {
      let results = await this.state.mapObj.themes.categories[1].getLayerByKey("lyr_public_fires_mvt").getFeaturesInView();
      this.updatePrintImage();

      results = results.map(this.convertOLRenderFeatureToTimelineEntry);

      if (results && results.length > 0) {
        let timelineEntries = results.sort(this.sortByDiscoveryDate);

        this.setState({ timelineEntries });

      }
      else {
        this.setState({ timelineEntries: [] });
      }
    }
    catch (ees) {
      console.error("Error getting timeline entries", ees);
    }

    this.setState({ searching: false });

  }

  openAboutModal = () => {
    this.setState({ modalOpen: true, modalContent: "about" });
  };

  closeModal = () => {
    this.setState({ modalOpen: false });
  }

  modalSearch = (address) => {
    this.setState({ addressState: { ...this.state.addressState, addressInput: address.display } });
    this.closeModal();
  }

  addressInputChange = (address) => {
    this.setState({ addressState: { ...this.state.addressState, addressInput: address } });
  }

  featureClicked = (features) => {

    if (!features || features.length === 0) {
      this.unhighlightAllFeatures();
      return;
    }

    let results = features.map(this.convertOLRenderFeatureToTimelineEntry);
    results = results.sort(this.sortByDiscoveryDate);


    let feature = results[0];


    // Try to get the element that matches the feature's id
    let el = document.getElementById(`timeline_${feature.id}`);

    let delay = 500;

    if (this.state.timelineHighlightId === feature.id) {
      delay = 0;
    }
    else {
      this.setState({ timelineHighlightId: feature.id });
      this.highlightFeatureById(feature.id);
    }

    // If the element exists, scroll to it
    if (el) {
      setTimeout(() => {
        el.scrollIntoView({ behavior: "smooth" });
      }, delay);
    }
  }

  timelineEntrySelected = (entry, event) => {
    window.scroll({ top: 0, behavior: 'smooth' });
    this.highlightFeatureById(entry.id);
  }

  timelineEntryDeselected = (event) => {
    this.unhighlightAllFeatures();
  }

  highlightFeatureById = (id) => {
    this.setState({ timelineHighlightId: id });
    this.state.mapObj.themes.categories[1].getLayerByKey("lyr_public_fires_mvt").unhighlightAll();
    this.state.mapObj.themes.categories[1].getLayerByKey("lyr_public_fires_mvt").highlight(id);
  }

  unhighlightAllFeatures = () => {
    this.setState({ timelineHighlightId: false });
    this.state.mapObj.themes.categories[1].getLayerByKey("lyr_public_fires_mvt").unhighlightAll();
  }

  // This will call to the browser print method
  printClicked = async () => {
    // TODO: Call the updatePrintImage function before printing
    await this.updatePrintImageAsync();
    window.print();
  }

  getModalContent = () => {
    if(this.state.modalContent === "search") {
      return (<AddressSearch onLocationFound={this.modalSearch} onAddressInputChange={this.addressInputChange} />);
    }
    else if(this.state.modalContent === "about") {
      return (<About />);
    }
    else {
      return (<div>Error</div>);
    }
  }

  renderStateDebug = () => {
    if(this.state.debugState) {
      return (
        <div className='stateDebug no-print'>
          <p>Width: { window.visualViewport.width } </p>
          <p>Height: { window.visualViewport.height } </p>
          <p>Canvases: { window.mapObj?.getViewport().querySelectorAll(canvasSelector).length } </p>
          <p>Printing: {this.state.printing ? 'Printing' : '' } </p>
          <p>Rendering: {this.state.rendering ? 'Rendering' : '' } </p>
          <p>Searching: {this.state.searching ? 'Searching' : ''} </p>
          <p>Zoom In: {this.state.zoomIn ? 'Zoom In' : ''} </p>
        </div>
      )
    }
  }

  renderTimeline = () => {
    if ( this.state.searching || this.state.rendering ) {
      return (<LoadingComponent />)
     } else {
      return ( <TimelineComponent 
        timelineEntries={this.state.timelineEntries} address={this.state.address} extent={this.state.extent} 
        zoomIn={this.state.zoomIn} onEntrySelected={this.timelineEntrySelected} onEntryDeselected={this.timelineEntryDeselected} 
        highlightId={this.state.timelineHighlightId} onPrintClicked={this.printClicked} />
      );
     }
  }

  render = () => {

    let modalContent = this.getModalContent();
    let timelineEntriesCount = this.state.timelineEntries ? this.state.timelineEntries.length : 0;

    return (
      <div className="App">
        <React.StrictMode>
        <MapContext.Provider value={this.state.mapObj}>
          <HeaderComponent openAbout={this.openAboutModal} />
          <div className="map-bar">
            <CollapsableSidebar categories={this.state.categories} />
            <MapComponent getMap={this.createMapIfNotExists} useCategories={this.useCategories} startAddress={this.state.addressState.addressInput} onFeatureClick={this.featureClicked} highlightId={this.state.timelineHighlightId} />
          </div>
          <EntryCountComponent address={this.state.address} extent={this.state.extent} timelineCount={timelineEntriesCount} zoomIn={this.state.zoomIn} searching={this.state.searching} />
          {this.renderStateDebug()}
          {this.renderTimeline()}
          <LearnMoreComponent lat={this.state.lat} lon={this.state.lon} zoom={this.state.zoom} />
          <Modal show={this.state.modalOpen} content={modalContent} closeModal={this.closeModal} />
          <div className={this.state.buildInfo ? 'version' : 'hidden'}>
            {this.state.buildInfo ? this.state.buildInfo.packageVersion + " " + this.state.buildInfo.buildNumber : ''}

          </div>
        </MapContext.Provider>
        </React.StrictMode>
      </div>
    );
  }
}

export default App;
